import {
  Component,
  effect,
  forwardRef,
  inject,
  Injector,
  OnInit,
  input,
  untracked,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { INTERNATIONAL_OPTIONS, NameValue } from '@ui/shared/model';

import { TranslatePipe } from '@ngx-translate/core';
import { ImmomioSelect } from '@immomio/ui/select';
import { ImmomioFormFieldComponent } from '@immomio/ui/form';
import { AppInputDirective } from '../input/input.directive';
import { HintComponent } from '../../../hint/hint.component';
import { FormFieldLabelComponent } from '../../form-field/form-field-label/form-field-label.component';
import { FormFieldComponent } from '../../form-field/form-field.component';
import { AppFormFieldControl } from '../../form-field/form-field-control/form-field-control';
import { BaseControl } from '../base-control';
import { RegexTypes } from '../../../../../utils/regex-types';

@Component({
  selector: 'app-international-phone',
  templateUrl: './international-phone.component.html',
  styleUrls: ['./international-phone.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InternationalPhoneComponent),
      multi: true
    },
    {
      provide: AppFormFieldControl,
      useExisting: forwardRef(() => InternationalPhoneComponent)
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FormFieldComponent,
    FormFieldLabelComponent,
    ImmomioFormFieldComponent,
    HintComponent,
    ImmomioSelect,
    AppInputDirective,
    TranslatePipe
  ]
})
export class InternationalPhoneComponent
  extends BaseControl<string>
  implements OnInit, ControlValueAccessor
{
  #destroyRef = inject(DestroyRef);
  #injector = inject(Injector);
  private fb = inject(FormBuilder);
  readonly readonly = input(false);
  public form: FormGroup;
  readonly internationalOptions = input<NameValue[]>(INTERNATIONAL_OPTIONS);

  private _errors: any;
  private _touched = false;

  get errors() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this._errors;
  }

  set errors(errors: any) {
    this._errors = errors;
  }

  get touched() {
    return this._touched;
  }

  set touched(value: boolean) {
    this._touched = value;
  }

  private splitNumber(value: string) {
    // This regex is used to split the country code and phone number
    const regex = new RegExp(RegexTypes.INTERNATIONAL_PHONE);
    const payload = {
      international: '+49',
      number: ''
    };
    if (value) {
      const regexResults = value.match(regex);
      payload.international = regexResults[0];
      payload.number = regexResults[1];
    }

    if (value?.charAt(0) === '0') {
      payload.international = payload.international || '+49';
      payload.number = value.substring(1);
    }
    return payload;
  }

  /*
   * Cut first Zero and remove special characters
   */
  private static parseNumber(number: string) {
    let n = number;
    if (n.length === 0) {
      return n;
    }

    n = n.replace(RegexTypes.SPECIAL_CHARACTERS, '');

    if (n.charAt(0) !== '0') {
      return n;
    }

    while (n?.charAt(0) === '0') {
      n = n.substring(1);
    }
    return n;
  }

  private onChange = (value: unknown) => value;
  private onTouch = () => null;

  public ngOnInit() {
    this.form = this.fb.group({
      international: '',
      number: ''
    });

    effect(
      () => {
        const isReadonly = this.readonly();
        untracked(() => {
          const internationalControl = this.form.controls.international;
          if (isReadonly) {
            internationalControl.disable({ emitEvent: false });
          } else {
            internationalControl.enable({ emitEvent: false });
          }
        });
      },
      { injector: this.#injector }
    );

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((value: { international: string; number: string }) => {
        if (!value.international || !value.number) {
          // Change the formcontrol value to en empty string in order to trigger validations
          this.onChange('');
          return;
        }

        // Combine both values again and set them as value for the formcontrol
        this.onChange(`${String(value.international)}${value.number}`);
        this.onTouch();
      });
  }

  public registerOnChange(fn) {
    this.onChange = fn;
  }

  public registerOnTouched(fn) {
    this.onTouch = fn;
  }

  public writeValue(value: string) {
    this.form.patchValue(this.splitNumber(value));
  }
}
